"use client";

import { useMemo, useState } from "react";
import { Check, Copy } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Components } from "react-markdown";

import { Button } from "@/shared/ui/shadcn-components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/shadcn-components/dialog";

const markdownComponents: Components = {
  h1: ({ children }) => (
    <h1 className="mt-4 text-[20px] font-semibold leading-7 tracking-tight text-foreground first:mt-0">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="mt-4 text-[17px] font-semibold leading-6 tracking-tight text-foreground first:mt-0">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="mt-3 text-[15px] font-semibold leading-6 text-foreground first:mt-0">
      {children}
    </h3>
  ),
  p: ({ children }) => <p className="my-1.5 leading-6 text-foreground/90">{children}</p>,
  ul: ({ children }) => <ul className="my-1.5 list-disc space-y-1 pl-5">{children}</ul>,
  ol: ({ children }) => <ol className="my-1.5 list-decimal space-y-1 pl-5">{children}</ol>,
  li: ({ children }) => <li className="leading-6">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
  code: ({ className, children }) => {
    const isBlock = Boolean(className?.includes("language-"));
    if (isBlock) {
      return (
        <code
          className={`${className ?? ""} whitespace-pre-wrap break-words [overflow-wrap:anywhere]`}
        >
          {children}
        </code>
      );
    }
    return (
      <code className="rounded bg-muted px-1 py-0.5 font-mono text-[0.9em] break-all text-foreground">
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="my-2 max-w-full min-w-0 overflow-x-auto rounded-lg bg-muted/50 px-3 py-2 font-mono text-[12px] leading-5 text-foreground/90 whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <div className="my-2 max-w-full min-w-0 overflow-x-auto">
      <table className="w-full border-collapse text-left text-[13px]">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border-b border-border px-2 py-1.5 font-semibold text-foreground">{children}</th>
  ),
  td: ({ children }) => (
    <td className="border-b border-border/60 px-2 py-1.5 align-top text-foreground/90">
      {children}
    </td>
  ),
  hr: () => <hr className="my-3 border-border/60" />,
};

export function InsightsInvestigationReportDialog({
  open,
  onOpenChange,
  markdown,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  markdown: string;
}) {
  const [copied, setCopied] = useState(false);
  const body = useMemo(() => markdown, [markdown]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[min(90vh,720px)] w-full max-w-[calc(100%-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl"
        showCloseButton
      >
        <DialogHeader className="shrink-0 border-b border-border/60 px-5 py-4 pr-12">
          <DialogTitle>Investigation report</DialogTitle>
          <DialogDescription>
            Summary of this session (coverage, questions, tasks, errors, timing).
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden px-5 py-4">
          <div className="min-w-0 space-y-1 text-[14px] leading-6 text-foreground [&_:first-child]:mt-0">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {body}
            </ReactMarkdown>
          </div>
        </div>
        <DialogFooter className="shrink-0 sm:justify-between" showCloseButton>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => void copy()}
          >
            {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
            {copied ? "Copied" : "Copy markdown"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
